import { RealTime } from 'Roblox';
import React, { useState, useEffect } from 'react';
import { urlService } from 'core-utilities';
import { Link, Loading } from 'react-style-guide';
import PropTypes from 'prop-types';
import gamesService from '../../gameData/services/gamesService';
import gamesApiConstants from '../../gameData/constants/gamesApiConstants';
import ListOfGamesComponent from '../components/ListOfGamesComponent';
import GameContainerHeader from '../components/GameContainerHeader';
import placeListsLayout from '../constants/placeListsLayout';
import '../../../../css/gameLists/gameLists.scss';

function EachGamelistContainer({
  sort,
  pageContext,
  sortPosition,
  timeFilters,
  friendsData,
  pageType,
  ...props
}) {
  const { translate } = props;
  const [gamesData, setGamesData] = useState([]);
  const {
    token,
    displayName,
    name,
    gameSetTargetId,
    contextCountryRegionId,
    timeOptionsAvailable,
    numberOfGames
  } = sort;
  const { params } = gamesApiConstants;
  const initializedApiParams = {
    sortToken: token,
    gameSetTargetId,
    startRows: params.startRows,
    maxRows: numberOfGames || gamesApiConstants.pageSizeOfHomeSorts,
    hasMoreRows: true,
    sortPosition,
    contextCountryRegionId
  };
  if (timeOptionsAvailable) {
    initializedApiParams.timeFilter = gamesService.getTimeFilterToken(timeFilters);
  }
  initializedApiParams['pageContext.pageId'] = pageContext?.pageId;
  const [sortApiParams, setSortApiParams] = useState(initializedApiParams);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isSortEmpty, setIsSortEmpty] = useState(false);
  const [isInitializedLoading, setInitializedLoading] = useState(false);

  const loadGamesList = isInitialized => {
    if (isInitialized) {
      setInitializedLoading(true);
    }
    gamesService
      .getGamesList(sortApiParams)
      .then(
        result => {
          if (result?.games?.length) {
            const gamesForRendering = result.games.slice(0, placeListsLayout.maxNumOfGamesPerList);
            const universeIds = [];
            gamesForRendering.forEach(game => {
              universeIds.push(game.universeId);
            });
            gamesService.getGamesPlayabilityStatus(universeIds).then(
              response => {
                const { data } = response;
                data.forEach(playabilityStatus => {
                  gamesForRendering.forEach(game => {
                    if (game.universeId === playabilityStatus.universeId) {
                      Object.assign(game, playabilityStatus);
                    }
                  });
                });

                setGamesData(gamesForRendering);
              },
              error => {
                console.debug(error);
              }
            );
          } else {
            setIsSortEmpty(true);
          }
        },
        error => {
          console.debug(error);
        }
      )
      .finally(() => {
        setIsDataLoaded(true);
        if (isInitialized) {
          setInitializedLoading(false);
        }
      });
  };
  useEffect(() => {
    loadGamesList(true);
    return () => {};

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGameListRefresh = () => {
    const sortValue = gamesService.getSortValueFromStorage(name);
    if (sortValue) {
      const isSortTokenExpired = gamesService.isSortTokenExpired(sortValue);
      if (isSortTokenExpired) {
        gamesService.getGameSortBySortName(name, gamesApiConstants.gamesHomeSortAlgorithm).then(
          sortData => {
            setSortApiParams(currentApiParams => {
              const updatedApiParams = currentApiParams;
              updatedApiParams.sortToken = sortData.token;
              updatedApiParams.gameSetTargetId = sortData.gameSetTargetId;
              return updatedApiParams;
            });
            loadGamesList();
          },
          error => {
            console.debug(error);
          }
        );
      } else {
        loadGamesList();
      }
    }
  };

  const realTimeClient = RealTime.Factory.GetClient();
  useEffect(() => {
    const {
      notificationTypes: { gameFavoriteNotifications, gameCloseNotifications },
      sortsNeedRealtimeUpdate: { myFavorite, myRecent }
    } = placeListsLayout;

    if (name === myFavorite) {
      realTimeClient.Subscribe(gameFavoriteNotifications, handleGameListRefresh);
    }

    if (name === myRecent) {
      realTimeClient.Subscribe(gameCloseNotifications, handleGameListRefresh);
    }
    return () => {
      if (name === myFavorite) {
        realTimeClient.Unsubscribe(gameFavoriteNotifications, handleGameListRefresh);
      }
      if (name === myRecent) {
        realTimeClient.Unsubscribe(gameCloseNotifications, handleGameListRefresh);
      }
    };
  }, []);
  const seeAllLink = urlService.formatUrl(`/games/#sortName/${sort.name}`);

  return (
    <div className='col-xs-12 container-list places-list'>
      <Link url={seeAllLink}>
        <GameContainerHeader headerLabel={displayName} {...props} />
      </Link>
      {isInitializedLoading && <Loading />}
      {isSortEmpty && isDataLoaded && !isInitializedLoading && (
        <p className='section-content-off'>{translate(placeListsLayout.NoGamesLabel)}</p>
      )}
      {gamesData.length > 0 && (
        <ListOfGamesComponent
          gamesData={gamesData}
          name={name}
          sort={sort}
          pageContext={pageContext}
          sortPosition={sortPosition}
          isInitializedLoading={isInitializedLoading}
          friendsData={friendsData}
          pageType={pageType}
          {...props}
        />
      )}
    </div>
  );
}

EachGamelistContainer.defaultProps = {
  friendsData: [],
  pageType: ''
};

EachGamelistContainer.propTypes = {
  sort: PropTypes.shape({
    token: PropTypes.string,
    displayName: PropTypes.string,
    name: PropTypes.string,
    numberOfRows: PropTypes.number,
    gameSetTargetId: PropTypes.number,
    contextCountryRegionId: PropTypes.number,
    timeOptionsAvailable: PropTypes.bool,
    numberOfGames: PropTypes.number
  }).isRequired,
  pageContext: PropTypes.shape({ pageId: PropTypes.string }).isRequired,
  sortPosition: PropTypes.number.isRequired,
  timeFilters: PropTypes.arrayOf(
    PropTypes.shape({ token: PropTypes.string, name: PropTypes.string })
  ).isRequired,
  translate: PropTypes.func.isRequired,
  friendsData: PropTypes.arrayOf(
    PropTypes.shape({
      presense: PropTypes.shape({ userPresenceType: PropTypes.number }),
      id: PropTypes.number,
      nameForDisplay: PropTypes.string
    })
  ),
  pageType: PropTypes.string
};

export default EachGamelistContainer;
